import {
	CidrRanges,
	CidrRangeListItemProps,
	RadioButtonGroup,
	RadioButtonGroupItem,
	ValidationError,
} from "client/Components";
import { NoUniqueInputIdContext } from "client/Contexts/UniqueInputIdContext";
import { IInputAttachable, IValueInputAttachable } from "client/Forms/attachable";
import { getClientId, ObjectWithClientId, setClientId } from "client/Misc/clientId";
import { createValidationConfiguration } from "client/Validation";
import { IValidationConfiguration } from "client/Validation/builder";
import { InputValidationResult, InputValidationResultKind } from "client/Validation/input";
import { inline, isNotEmpty, isNotNullNorUndefined } from "client/Validation/rules";
import { ReactElement} from "react";
import { localize } from "client_legacy/ndaw/culture";
import styles from "./IpAddresses.module.less";
import { IpAddressRange } from "client/Api/common";

export type IpAddressesValue = {
	restrictIp: boolean;
	whitelistIpRanges: readonly ObjectWithClientId<IpAddressRange>[];
	blacklistIpRanges: readonly ObjectWithClientId<IpAddressRange>[];
};

export type IpAddressesProps = {
	attachable: IValueInputAttachable<IpAddressesValue>;
};

export const IpAddresses = (props: IpAddressesProps): ReactElement => {
	const {
		attachable
	} = props;

	return (
		<div>
			<NoUniqueInputIdContext>
				<RadioButtonGroup
					options={restrictIpOptions}
					{...attachable.for("restrictIp").attachToRadioButtonGroup()}
				/>

				<ValidationError {...attachable.attachToValidationError(true)} />
				{ attachable.for("restrictIp").value && (
					<div className={styles.ipLists}>
						<IpList
							title={localize("_Whitelist")}
							attachable={attachable.for("whitelistIpRanges")}
						/>

						<IpList
							title={localize("_Blacklist")}
							attachable={attachable.for("blacklistIpRanges")}
						/>
					</div>
				)}
			</NoUniqueInputIdContext>
		</div>
	);
};
IpAddresses.displayName = "IpAddresses";

const restrictIpOptions: readonly RadioButtonGroupItem<boolean>[] = [
	{
		text: localize("_AnyIPRange"),
		value: false
	},
	{
		text: localize("_IPRangeOrAddress"),
		value: true
	}
];

type IpListProps = {
	title: string;
	attachable: IValueInputAttachable<readonly IpAddressRange[]>;
};

const IpList = (props: IpListProps): ReactElement => {
	const {
		title,
		attachable
	} = props;

	const addHandler = (): void => {
		const newIpAddress: ObjectWithClientId<IpAddressRange> = setClientId({ value: "" });

		attachable
			.reducer
			.push(newIpAddress);
	};

	const removeHandler = (index: number): void => {
		attachable
			.reducer
			.removeAt(index);
	};

	return (
		<div className={styles.ipList}>
			<CidrRanges
				title={title}
				onAdd={addHandler}
			>
				{ attachable.map((x, i) => (
					<CidrRanges.Item
						key={i}
						{...toCidrRangeItemProps(x.for("value"))}
						onRemove={() => removeHandler(i)}
					/>
				))}
			</CidrRanges>
		</div>
	);
};
IpList.displayName = "IpList";

function toCidrRangeItemProps(
	attachable: IInputAttachable<string>
): Pick<CidrRangeListItemProps, "value" | "onChange" | "onBlur" | "validationMessage" | "trackingId"> {
	const {
		reducer,
		validation
	} = attachable;

	const validationState: InputValidationResult = validation?.getValidationState()
		?? { kind: InputValidationResultKind.Valid };

	return {
		value: reducer.value,
		onChange: (newValue: string) => {
			reducer.setValue(newValue);
			validation?.triggerValueChanged();
		},
		onBlur: () => {
			validation?.triggerInputFinished();
		},
		validationMessage: validationState.kind === InputValidationResultKind.Invalid
			? validationState.errorMessage
			: undefined,
		trackingId: reducer.path
	};
}

export function createIpAddressesValidation<TModel extends IpAddressesValue>(): IValidationConfiguration<TModel> {
	const ipRangeValidationBuilder: IValidationConfiguration<ObjectWithClientId<IpAddressRange>> = createValidationConfiguration<ObjectWithClientId<IpAddressRange>>();

	ipRangeValidationBuilder.forScalar("value")
		.add(
			isNotNullNorUndefined(),
			isNotEmpty()
		);

	const result: IValidationConfiguration<TModel> = createValidationConfiguration<TModel>();

	const ipvalidation: IValidationConfiguration<TModel> = result.when(x => x.restrictIp);

	ipvalidation.add(
		inline((value: TModel): InputValidationResult => {
			const whiteListIsEmpty: boolean = value.whitelistIpRanges.length === 0;
			const blackListIsEmpty: boolean = value.blacklistIpRanges.length === 0;

			if (whiteListIsEmpty && blackListIsEmpty) {
				return {
					kind: InputValidationResultKind.Invalid,
					errorMessage: localize("_NoIpAddressesIpRangesAreDefined")
				};
			}

			return {
				kind: InputValidationResultKind.Valid
			};
		})
	);

	ipvalidation.forCollection("whitelistIpRanges")
		.forItems(
			ipRangeValidationBuilder,
			x => getClientId(x)
		);

	ipvalidation.forCollection("blacklistIpRanges")
		.forItems(
			ipRangeValidationBuilder,
			x => getClientId(x)
	);

	return result;
}